import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppStore } from '@/src/state/app-store';

import {
  canUseWebPush,
  refreshGrantedWebPushToken,
  registerWebPushToken,
  revokeWebPushToken,
} from './web-push-service';
import { currentNotificationPermission, isStandalonePwa } from './pwa-service';

export function useWebPush() {
  const webPushDisabledAt = useAppStore((state) => state.webPushDisabledAt);
  const storedToken = useAppStore((state) => state.webPushToken);
  const setWebPushDisabledAt = useAppStore(
    (state) => state.setWebPushDisabledAt,
  );
  const setWebPushToken = useAppStore((state) => state.setWebPushToken);
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(() =>
    currentNotificationPermission(),
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;

    void canUseWebPush().then((nextSupported) => {
      if (!mounted) {
        return;
      }

      setSupported(nextSupported);
      const nextPermission = currentNotificationPermission();
      setPermission(nextPermission);

      if (
        nextSupported &&
        nextPermission === 'granted' &&
        !webPushDisabledAt
      ) {
        void refreshGrantedWebPushToken()
          .then((token) => {
            if (mounted && token) {
              setWebPushToken(token);
            }
          })
          .catch(() => undefined);
      }
    });

    return () => {
      mounted = false;
    };
  }, [setWebPushToken, webPushDisabledAt]);

  const enable = useCallback(async () => {
    setBusy(true);

    try {
      const token = await registerWebPushToken();

      setWebPushDisabledAt(null);
      setWebPushToken(token);
      setPermission(currentNotificationPermission());
      return token;
    } finally {
      setBusy(false);
    }
  }, [setWebPushDisabledAt, setWebPushToken]);

  const disable = useCallback(async () => {
    setBusy(true);

    try {
      await revokeWebPushToken(storedToken);
      setWebPushDisabledAt(new Date().toISOString());
      setWebPushToken(null);
      setPermission(currentNotificationPermission());
    } finally {
      setBusy(false);
    }
  }, [setWebPushDisabledAt, setWebPushToken, storedToken]);

  return useMemo(
    () => ({
      busy,
      disable,
      enable,
      enabledOnThisDevice: Boolean(
        storedToken && permission === 'granted' && !webPushDisabledAt,
      ),
      isStandalone: isStandalonePwa(),
      permission,
      supported,
    }),
    [busy, disable, enable, permission, storedToken, supported, webPushDisabledAt],
  );
}
